import { z } from "zod";

import {
  checklistSectionSchema,
  ctaBannerSectionSchema,
  heroSectionSchema,
  listSectionSchema,
  pageSeoSchema,
  profileSectionSchema,
} from "@/content/schemas";
import { siteConfig } from "@/content/site";
import { validateContent } from "@/content/validateContent";

const pricingSectionSchema = z.object({
  title: z.string().min(1),
  badge: z.string().min(1),
  price: z.string().optional(),
  items: z.array(z.string().min(1)).min(1),
  primaryCta: z.object({
    label: z.string().min(1),
    href: z.string().min(1),
    external: z.boolean(),
  }),
  footerNote: z.string().optional(),
});

const statementSectionSchema = z.object({
  title: z.string().min(1),
  paragraphs: z.array(z.string().min(1)).min(1),
  highlight: z.string().optional(),
});

const aiCareerAdvantagePageSchema = z.object({
  seo: pageSeoSchema,
  hero: heroSectionSchema,
  problemSection: statementSectionSchema,
  includedSection: checklistSectionSchema,
  benefitsSection: listSectionSchema,
  midPageCta: ctaBannerSectionSchema,
  audienceSection: checklistSectionSchema,
  instructorSection: profileSectionSchema,
  pricingSection: pricingSectionSchema,
  finalCta: ctaBannerSectionSchema,
});

const bundleCheckoutCta = {
  label: "Get the Complete AI Career Advantage Bundle",
  href: siteConfig.aiCareerAdvantageStripeUrl,
  external: true,
};

export const aiCareerAdvantagePageContent = validateContent(
  "aiCareerAdvantagePage",
  aiCareerAdvantagePageSchema,
  {
    seo: {
      title: "AI Career Advantage Bundle | Tech Savvy Starts Here",
      description:
        "Get the AI Career Advantage Guide and Toolkit. Practical prompts, templates, worksheets, and a 30-day plan to use AI more productively at work.",
      canonicalPath: "/ai-career-advantage",
      socialImage: siteConfig.aiCareerAdvantageMockupImage,
    },
    hero: {
      eyebrow: "Digital Product",
      title: "Use AI to Work Faster",
      accent: "and Stand Out at Work",
      description:
        "Stop guessing what to type into ChatGPT. Get ready-to-use prompts, templates, workflows, and a simple plan for applying AI to the work you already do.",
      secondaryDescription:
        "The AI Career Advantage Bundle is a practical guide and toolkit for busy professionals who want better results from AI without becoming technical experts.",
      primaryCta: {
        ...bundleCheckoutCta,
        label: "Get the Bundle",
      },
      secondaryCta: {
        label: "See What's Inside",
        href: "#inside",
        external: false,
      },
      media: {
        type: "image",
        imageSrc: siteConfig.aiCareerAdvantageMockupImage,
        imageAlt: "The AI Career Advantage Guide and Toolkit product bundle mockup",
        imageFit: "contain",
        floatingBadge: {
          icon: "Gift",
          title: "Complete Bundle",
          description: "Guide + Toolkit included",
        },
      },
    },
    problemSection: {
      title: "AI Should Save Time, Not Create More Work",
      paragraphs: [
        "You know AI can help, but the results are often too generic, too vague, or too hard to use.",
        "So you rewrite prompts, test different tools, edit the output, and still spend too much time figuring out how to make AI useful.",
        "This bundle gives you a clearer starting point: practical resources built around real workplace tasks.",
      ],
    },
    includedSection: {
      title: "What's Inside the Bundle",
      description:
        "A complete digital resource kit for using AI more effectively at work.",
      items: [
        "AI Career Advantage Guide",
        "AI Career Advantage Toolkit",
        "100 professional AI prompts",
        "Executive email prompt library",
        "Meeting preparation templates",
        "Career visibility worksheet",
        "Weekly AI productivity planner",
        "2026 AI tool directory",
        "30-day AI career accelerator plan",
      ],
    },
    benefitsSection: {
      title: "What This Helps You Do",
      description:
        "Use AI with more confidence across the tasks that take up your week.",
      items: [
        {
          title: "Write faster",
          description:
            "Draft clearer emails, reports, updates, summaries, and professional messages.",
          icon: "FileText",
        },
        {
          title: "Prepare better",
          description:
            "Plan meetings, organize ideas, research topics, and shape stronger presentations.",
          icon: "Calendar",
        },
        {
          title: "Work smarter",
          description:
            "Turn useful prompts into repeatable workflows you can use again and again.",
          icon: "Target",
        },
      ],
    },
    midPageCta: {
      title: "Ready to Put These AI Tools to Work?",
      description:
        "Get the complete bundle now and start using the prompts, templates, and 30-day plan in your real workday.",
      primaryCta: {
        ...bundleCheckoutCta,
        label: "Buy the Bundle",
      },
      tone: "light",
    },
    audienceSection: {
      title: "This Is for You If...",
      accentLine: "No technical background required.",
      items: [
        "You want AI to save you more time",
        "You keep getting generic AI responses",
        "You are overwhelmed by all the AI tools",
        "You write, research, plan, or prepare often at work",
        "You want better output without longer workdays",
        "You are new to AI or ready to use it more seriously",
      ],
    },
    instructorSection: {
      title: "Created by Dr. Ijeoma Ndu",
      name: "Dr. Ijeoma Ndu",
      role: "ML Engineer and AI Educator",
      description:
        "Dr. Ijeoma Ndu is an AI and machine learning professional, educator, and technology leader who helps professionals move past AI hype and use AI in practical, useful ways at work.",
      icon: "User",
      imageSrc: siteConfig.headshotImage,
      imageAlt: "Dr. Ijeoma Ndu headshot",
    },
    pricingSection: {
      title: "Get Instant Access to the AI Career Advantage Bundle",
      badge: "Digital download",
      price: "USD $29",
      items: [
        "Complete guide and toolkit",
        "Practical workplace prompts",
        "Templates, worksheets, and tool recommendations",
        "30-day implementation plan",
      ],
      primaryCta: bundleCheckoutCta,
      footerNote:
        "Start using AI more productively at work. No technical background required.",
    },
    finalCta: {
      title: "Ready to Make AI More Useful at Work?",
      description:
        "Get the prompts, templates, and plan to start using AI with more confidence.",
      primaryCta: {
        ...bundleCheckoutCta,
        label: "Get the Bundle",
      },
      secondaryCta: {
        label: "Questions? Contact Us",
        href: "/contact",
        external: false,
      },
      tone: "dark",
    },
  },
);
